/**
 * Theme Generator Utility
 * Generates Material-UI themes from tenant configuration colors
 */

import { createTheme } from '@mui/material/styles';
import theme from '../theme/theme';

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color string
 * @returns {Object|null} RGB object or null
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

/**
 * Convert RGB to hex color
 * @param {number} r - Red value
 * @param {number} g - Green value
 * @param {number} b - Blue value
 * @returns {string} Hex color string
 */
const rgbToHex = (r, g, b) => {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
};

/**
 * Lighten a hex color
 * @param {string} hex - Hex color string
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export const lightenColor = (hex, percent = 20) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(
    rgb.r + (255 - rgb.r) * (percent / 100),
    rgb.g + (255 - rgb.g) * (percent / 100),
    rgb.b + (255 - rgb.b) * (percent / 100)
  );
};

/**
 * Darken a hex color
 * @param {string} hex - Hex color string
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export const darkenColor = (hex, percent = 20) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(
    rgb.r * (1 - percent / 100),
    rgb.g * (1 - percent / 100),
    rgb.b * (1 - percent / 100)
  );
};

/**
 * Calculate contrasting text color for a background
 * @param {string} backgroundColor - Hex color string
 * @returns {string} Either white or black hex color
 */
export const getContrastText = (backgroundColor) => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#ffffff';
  // Calculate luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Generate a full Material-UI theme from tenant configuration
 * @param {Object} tenantTheme - Tenant theme configuration
 * @param {string} tenantTheme.primaryColor - Primary color (required)
 * @param {string} tenantTheme.primaryLight - Primary light variant
 * @param {string} tenantTheme.primaryDark - Primary dark variant
 * @param {string} tenantTheme.secondaryColor - Secondary color (required)
 * @param {string} tenantTheme.secondaryLight - Secondary light variant
 * @param {string} tenantTheme.secondaryDark - Secondary dark variant
 * @param {string} tenantTheme.sidebarColor - Sidebar background color
 * @returns {Object} Material-UI theme object
 */
export const generateThemeFromTenantConfig = (tenantTheme) => {
  if (!tenantTheme || !tenantTheme.primaryColor) {
    return theme; // Return default theme
  }

  const primaryColor = tenantTheme.primaryColor;
  const secondaryColor = tenantTheme.secondaryColor || '#dc004e';

  // Generate light/dark variants if not provided
  const primaryLight = tenantTheme.primaryLight || lightenColor(primaryColor, 20);
  const primaryDark = tenantTheme.primaryDark || darkenColor(primaryColor, 20);
  const secondaryLight = tenantTheme.secondaryLight || lightenColor(secondaryColor, 20);
  const secondaryDark = tenantTheme.secondaryDark || darkenColor(secondaryColor, 20);
  const sidebarColor = tenantTheme.sidebarColor || '#1e293b';
  const appbarColor = tenantTheme.appbarColor || '#0f172a';

  return createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      primary: {
        main: primaryColor,
        light: primaryLight,
        dark: primaryDark,
        contrastText: getContrastText(primaryColor),
      },
      secondary: {
        main: secondaryColor,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: getContrastText(secondaryColor),
      },
      background: {
        ...theme.palette.background,
        sidebar: sidebarColor,
        header: appbarColor,
      },
    },
  });
};

/**
 * Generate theme metadata for display in theme selectors
 * @param {Object} tenantTheme - Tenant theme configuration
 * @param {string} tenantName - Tenant name for display
 * @returns {Object} Theme metadata object
 */
export const generateThemeMetadata = (tenantTheme, tenantName) => {
  if (!tenantTheme) return null;

  return {
    id: 'tenant',
    name: tenantName || 'Custom Theme',
    description: `${tenantName || 'Tenant'} corporate branding`,
    primaryColor: tenantTheme.primaryColor,
    secondaryColor: tenantTheme.secondaryColor,
    isTenantTheme: true,
  };
};

export default generateThemeFromTenantConfig;
